import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core'
import { FlexLayoutModule } from '@angular/flex-layout'
import { MatCardModule } from '@angular/material/card'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { RouterModule } from '@angular/router'
import { EstimateBalance } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'

import { balanceCptCodes, balancesOutstandingTotal, BalanceOutstandingPipe, patientBalancePath } from '../estimate-balance'

export interface BalanceRow {
  id: string
  balance: EstimateBalance
  href: string | null
  cptCodes: string[]
}

@Component({
  selector: 'mmx-balance-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FlexLayoutModule,
    MatCardModule,
    MatProgressBarModule,
    RouterModule,
    TranslateModule,
    BalanceOutstandingPipe,
  ],
  templateUrl: './balance-card.component.html',
  styleUrl: './balance-card.component.scss',
})
export class BalanceCardComponent implements OnChanges {
  @Input() balances: EstimateBalance[] | null = null
  @Input() loading = false
  @Input() error: string | null = null
  @Input() patientId: string | null = null

  // Recomputed only when balances or patientId change; rows carry stable `href` arrays so
  // routerLink's reference-diff doesn't refire on every CD tick.
  rows: BalanceRow[] = []
  outstandingTotal = 0
  hasBalances = false
  isEmpty = false

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['balances'] || changes['patientId']) {
      this.rows = (this.balances ?? []).map(balance => ({
        id: balance.id,
        balance,
        href: this.patientId ? patientBalancePath(this.patientId, balance.id) : null,
        cptCodes: balanceCptCodes(balance),
      }))
      this.outstandingTotal = balancesOutstandingTotal(this.balances)
      this.hasBalances = this.rows.length > 0
    }
    this.isEmpty = !this.loading && this.error == null && Array.isArray(this.balances) && this.balances.length === 0
  }

  trackRow(_index: number, row: BalanceRow): string {
    return row.id
  }
}
